<!--
@component
User or workspace avatar image with a deterministic letter/illustration fallback.

@use-instead-of raw <img class="rounded-full">
@figma https://www.figma.com/design/xb1BaksrwtGhe3JHzX1BbT/Design-System?node-id=2050-8176
-->
<script lang="ts">
  import defaultAvatar1Img from "./default-avatar-1.svg?no-inline";
  import defaultAvatar2Img from "./default-avatar-2.svg?no-inline";
  import defaultAvatar3Img from "./default-avatar-3.svg?no-inline";
  import defaultAvatar4Img from "./default-avatar-4.svg?no-inline";
  import defaultAvatar5Img from "./default-avatar-5.svg?no-inline";
  import defaultAvatar6Img from "./default-avatar-6.svg?no-inline";

  interface Props {
    avatarUrl?: string;
    size: number;
    name: string;
    disableBorder?: boolean;
    loading?: boolean;
  }

  let {
    avatarUrl = "",
    size,
    name,
    disableBorder = false,
    loading = false,
  }: Props = $props();

  let letter = $derived(name[0] ?? "?");

  let visualSize = $derived(size - (disableBorder ? 0 : 2)); // account for border in size
  let fontSize = $derived(visualSize / 1.6); // scale font so it doesn't look weird with img size

  const defaultImageByName = (n: string) => {
    const images = [
      defaultAvatar1Img,
      defaultAvatar2Img,
      defaultAvatar3Img,
      defaultAvatar4Img,
      defaultAvatar5Img,
      defaultAvatar6Img,
    ];
    return images[n.charCodeAt(0) % 6];
  };
</script>

<div class="avatar-wrapper" style="width: {size}px; height: {size}px;">
  {#if avatarUrl}
    <img
      class="avatar-icon"
      class:has-border={!disableBorder}
      style="width: {size}px; height: {size}px;"
      src={avatarUrl}
      alt="User avatar"
    />
  {:else}
    <div
      class="avatar-icon relative inline-flex shrink-0 items-center justify-center overflow-clip select-none"
      class:has-border={!disableBorder}
      style="width: {size}px; height: {size}px; font-size: {fontSize}px;"
    >
      <img
        class="absolute inset-0 h-full w-full object-cover"
        src={defaultImageByName(letter)}
        alt="User avatar"
      />
      <span
        data-theme="dark"
        class="z-1 leading-none font-medium text-primary uppercase"
        >{letter}</span
      >
    </div>
  {/if}
  {#if loading}
    <div class="shimmer-overlay" aria-hidden="true"></div>
  {/if}
</div>

<style lang="postcss">
  @reference "$app.css";

  .avatar-wrapper {
    @apply relative inline-flex shrink-0 align-middle;
  }

  .avatar-icon {
    @apply rounded-full object-cover;
  }

  .avatar-icon.has-border {
    @apply border border-secondary;
  }

  .shimmer-overlay {
    @apply pointer-events-none absolute inset-0 z-10 overflow-hidden rounded-full bg-secondary;
  }

  .shimmer-overlay::after {
    content: "";
    @apply absolute inset-0;
    background: linear-gradient(
      180deg,
      transparent 0%,
      var(--semantic-background-secondary-hover) 50%,
      transparent 100%
    );
    will-change: transform;
    animation: shimmer 1.2s ease-in-out infinite;
  }

  @keyframes shimmer {
    from {
      transform: translateY(-100%);
    }
    to {
      transform: translateY(100%);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .shimmer-overlay::after {
      animation: none;
    }
  }
</style>
